import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';

const STATS = [
  { value: '2', label: '影视主类别', desc: '电影与电视剧' },
  { value: '16+', label: '示范作品资料', desc: '含完整元信息' },
  { value: '20+', label: '类型与地区标签', desc: '支持多维筛选' },
  { value: '8', label: '资讯示例文章', desc: '指南与观察' },
];

const CONTACTS = [
  { icon: 'ri-mail-line', label: '合作与反馈邮箱', value: 'hello@kuaihuo-demo.example' },
  { icon: 'ri-weibo-line', label: '微博（示例账号）', value: '@快活影院' },
  { icon: 'ri-wechat-line', label: '微信公众号（示例）', value: 'kuaihuo_demo' },
  { icon: 'ri-map-pin-line', label: '办公地址（虚构）', value: '示例市示例区光影路 88 号' },
];

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 快活影院',
    '了解快活影院的品牌定位、内容范围与联系方式。快活影院是一个影视内容展示平台，提供电影、电视剧资料与观影资讯。',
    '关于快活影院,影视平台介绍,联系方式,使用声明',
  );

  return (
    <div className="w-full pb-4">
      {/* hero */}
      <div className="w-full bg-primary-950">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="面包屑" className="text-sm text-background-200/80">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-accent-400 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-background-50">关于我们</li>
            </ol>
          </nav>
          <h1 className="mt-5 font-heading font-extrabold text-3xl md:text-4xl text-background-50">
            关于快活影院
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-background-100/90">
            把散落各处的影视资料、观影建议与行业动态，整理成一份随时可以翻阅的观影地图。
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        {/* stats */}
        <section aria-label="平台数据" className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {STATS.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="font-heading font-extrabold text-3xl text-primary-600">{stat.value}</p>
              <p className="mt-2 text-sm font-semibold text-foreground-900">{stat.label}</p>
              <p className="mt-1 text-xs text-foreground-500">{stat.desc}</p>
            </div>
          ))}
        </section>

        {/* story */}
        <section aria-label="品牌故事" className="mt-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start">
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              我们的定位与初衷
            </h2>
            <div className="space-y-4 text-[15px] leading-8 text-foreground-700">
              <p>
                快活影院诞生于一个很朴素的念头：当影视内容越来越多，真正稀缺的不是“有什么可看”，
                而是“到底该看什么”。面对动辄成百上千的作品列表，观众往往在第一屏就迷失了方向。
              </p>
              <p>
                所以我们不做播放器，也不做社交社区。我们只专注一件事——把每一部作品整理清楚：
                它的基本信息、创作团队、适合谁看、以及为什么值得一个晚上。我们把这类信息称作“观影前的准备”。
              </p>
              <p>
                在此基础上，我们用类型、地区、年份等维度搭起了分类与筛选，再配上观影指南与行业观察，
                希望无论你是想找一部轻松的合家欢，还是想认真研究一种类型片，都能在这里找到入口。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-film-line"></i>
                浏览电影频道
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-newspaper-line"></i>
                阅读影视资讯
              </Link>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">内容范围说明</h2>
            <ul className="space-y-3">
              {[
                '覆盖电影与电视剧两大主类别',
                '收录封面、年份、类型、导演与演员等基础资料',
                '提供类型、地区、年份的组合筛选与关键词搜索',
                '提供观影指南、幕后制作与行业观察类资讯',
              ].map((text) => (
                <li key={text} className="flex gap-2.5 text-sm leading-6 text-foreground-700">
                  <i className="ri-check-line text-primary-500 mt-0.5"></i>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 pt-4 border-t border-background-200 text-xs leading-6 text-foreground-500">
              本站为演示站点，不涉及播放、下载与会员功能；所有作品资料、评论与资讯均为示例内容。
            </p>
          </div>
        </section>

        {/* contact */}
        <section aria-label="联系方式" className="mt-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">联系我们</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CONTACTS.map((contact) => (
              <div key={contact.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className={`${contact.icon} text-lg`}></i>
                </span>
                <p className="mt-3 text-xs text-foreground-500">{contact.label}</p>
                <p className="mt-1 text-sm text-foreground-900 break-all">{contact.value}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-foreground-400">
            * 以上联系方式均为演示用虚拟示例，请勿用于真实联络。
          </p>
        </section>

        {/* policy */}
        <section aria-label="使用声明与隐私政策" className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-file-text-line text-primary-500"></i>
              使用声明
            </h2>
            <div className="space-y-3 text-sm leading-7 text-foreground-700">
              <p>
                本站为影视内容展示演示站点，所展示的作品名称、海报、简介、评分与评论均为虚构示例，
                不代表任何真实作品，也不构成任何推荐或评价。
              </p>
              <p>
                本站不提供在线播放、下载、会员付费等功能。页面中的“在线观看”类按钮仅为前端交互演示占位，
                点击后不会加载任何媒体资源。
              </p>
              <p>
                您可以自由浏览本站内容用于了解产品形态，但不得将示例内容用于任何形式的商业传播。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-shield-check-line text-secondary-500"></i>
              隐私政策概要
            </h2>
            <div className="space-y-3 text-sm leading-7 text-foreground-700">
              <p>
                本站当前为纯前端演示项目，不设用户注册与登录，也不会主动收集、存储或上传您的个人身份信息。
              </p>
              <p>
                搜索关键词、筛选条件等操作仅在您的浏览器本地完成，用于即时展示结果，不会被持久化保存。
              </p>
              <p>
                若未来引入数据存储或统计分析能力，我们会在页面显著位置更新本概要，并说明数据的用途与范围。
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}